import React, { useState, useEffect } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import Modal from '../common/modals/Modal';
import { useTheme } from '../../context/ThemeContext';
import { apiPost, apiPatch } from '../../utils/api';
import toast from 'react-hot-toast';
import US_STATES from '../../constants/usStates';

const EMPTY_CONTACT = { name: '', email: '', phone: '', role: '', primary_contact: false };

const AuditManualEntryForm = ({ isOpen, onClose, auditId, editProperty = null, onSaved }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const isEdit = !!editProperty;

  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    property_name: '',
    property_address: '',
    state: '',
    management_company: '',
    audit_month: '',
    qty_units: '',
    qty_affordable_units: '',
    due_date: '',
    extended_use: false,
    comments: '',
  });
  const [contacts, setContacts] = useState([{ ...EMPTY_CONTACT }]);

  useEffect(() => {
    if (editProperty) {
      setForm({
        property_name: editProperty.property_name || '',
        property_address: editProperty.property_address || '',
        state: editProperty.state || '',
        management_company: editProperty.management_company || '',
        audit_month: editProperty.audit_month || '',
        qty_units: editProperty.qty_units || '',
        qty_affordable_units: editProperty.qty_affordable_units || '',
        due_date: editProperty.due_date || '',
        extended_use: editProperty.extended_use || false,
        comments: editProperty.comments || '',
      });
    } else {
      setForm({
        property_name: '', property_address: '', state: '', management_company: '',
        audit_month: '', qty_units: '', qty_affordable_units: '', due_date: '',
        extended_use: false, comments: '',
      });
      setContacts([{ ...EMPTY_CONTACT }]);
    }
  }, [editProperty, isOpen]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleContactChange = (idx, field, value) => {
    setContacts((prev) => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
  };

  const addContact = () => setContacts((prev) => [...prev, { ...EMPTY_CONTACT }]);

  const removeContact = (idx) => {
    if (contacts.length <= 1) return;
    setContacts((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (!form.property_name.trim()) {
      toast.error('Property name is required');
      return;
    }

    setSaving(true);
    try {
      let response;
      if (isEdit) {
        response = await apiPatch(`/admin/audit_tracker/properties/${editProperty.id}`, form);
      } else {
        const validContacts = contacts.filter((c) => c.name || c.email);
        response = await apiPost(`/admin/audit_tracker/audits/${auditId}/properties`, {
          ...form,
          audit_month: form.audit_month ? parseInt(form.audit_month) : null,
          qty_units: form.qty_units ? parseInt(form.qty_units) : null,
          qty_affordable_units: form.qty_affordable_units ? parseInt(form.qty_affordable_units) : null,
          contacts: validContacts,
        });
      }

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to save');
      }

      const data = await response.json();
      toast.success(data.message);
      if (onSaved) onSaved(data.property);
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const inputClass = `w-full px-3 py-2 rounded-lg border text-sm ${
    isDark
      ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-400'
      : 'bg-white border-gray-300 text-gray-700 placeholder-gray-400'
  } focus:outline-none focus:ring-2 focus:ring-blue-500`;

  const labelClass = `block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit Property' : 'Add Property'}
      size="xl"
      footer={
        <>
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-lg text-sm ${isDark ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2 rounded-lg text-sm bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
          >
            {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
          </button>
        </>
      }
    >
      <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
        {/* Property details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Property Name *</label>
            <input
              type="text"
              value={form.property_name}
              onChange={(e) => handleChange('property_name', e.target.value)}
              className={inputClass}
              placeholder="Oak Ridge Apartments"
            />
          </div>
          <div>
            <label className={labelClass}>Management Company</label>
            <input
              type="text"
              value={form.management_company}
              onChange={(e) => handleChange('management_company', e.target.value)}
              className={inputClass}
            />
          </div>
          <div className="md:col-span-2">
            <label className={labelClass}>Address</label>
            <input
              type="text"
              value={form.property_address}
              onChange={(e) => handleChange('property_address', e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>State</label>
            <select
              value={form.state}
              onChange={(e) => handleChange('state', e.target.value)}
              className={inputClass}
            >
              <option value="">Select state</option>
              {US_STATES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Audit Month</label>
            <select
              value={form.audit_month}
              onChange={(e) => handleChange('audit_month', e.target.value)}
              className={inputClass}
            >
              <option value="">Select month</option>
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {new Date(2000, i).toLocaleString('default', { month: 'long' })}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Total Units</label>
            <input
              type="number"
              value={form.qty_units}
              onChange={(e) => handleChange('qty_units', e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Affordable Units</label>
            <input
              type="number"
              value={form.qty_affordable_units}
              onChange={(e) => handleChange('qty_affordable_units', e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Due Date</label>
            <input
              type="date"
              value={form.due_date}
              onChange={(e) => handleChange('due_date', e.target.value)}
              className={inputClass}
            />
          </div>
          <div className="flex items-center gap-2 pt-5">
            <input
              type="checkbox"
              checked={form.extended_use}
              onChange={(e) => handleChange('extended_use', e.target.checked)}
              className="rounded border-gray-300"
            />
            <label className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Extended Use</label>
          </div>
        </div>

        {/* Comments */}
        <div>
          <label className={labelClass}>Comments</label>
          <textarea
            value={form.comments}
            onChange={(e) => handleChange('comments', e.target.value)}
            className={`${inputClass} h-20 resize-none`}
          />
        </div>

        {/* Contacts (create mode only) */}
        {!isEdit && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Contacts</label>
              <button
                onClick={addContact}
                className="flex items-center gap-1 text-xs text-blue-500 hover:text-blue-600"
              >
                <Plus size={14} /> Add Contact
              </button>
            </div>
            <div className="space-y-3">
              {contacts.map((contact, idx) => (
                <div key={idx} className={`flex items-start gap-2 p-3 rounded-lg ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                  <div className="flex-1 grid grid-cols-2 gap-2">
                    <input
                      type="text"
                      placeholder="Name"
                      value={contact.name}
                      onChange={(e) => handleContactChange(idx, 'name', e.target.value)}
                      className={inputClass}
                    />
                    <input
                      type="email"
                      placeholder="Email"
                      value={contact.email}
                      onChange={(e) => handleContactChange(idx, 'email', e.target.value)}
                      className={inputClass}
                    />
                    <input
                      type="text"
                      placeholder="Phone"
                      value={contact.phone}
                      onChange={(e) => handleContactChange(idx, 'phone', e.target.value)}
                      className={inputClass}
                    />
                    <input
                      type="text"
                      placeholder="Role (e.g. Property Manager)"
                      value={contact.role}
                      onChange={(e) => handleContactChange(idx, 'role', e.target.value)}
                      className={inputClass}
                    />
                  </div>
                  {contacts.length > 1 && (
                    <button
                      onClick={() => removeContact(idx)}
                      className="p-1 text-red-400 hover:text-red-500 mt-1"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default AuditManualEntryForm;
